import { useEffect, useState } from 'react';
import { KeyRound } from 'lucide-react';
import api from '../../../utils/api';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { apiErrorMessage } from '../../../utils/apiError';
import { dateOnly, formatLagos } from '../../../utils/datetime';
import { Alert, Button, CopyButton, EmptyState, Skeleton } from '../../ui';

const ROTATION_WARNING =
  'Rotating issues a new API key and the old key stops working immediately, so any integration still sending it starts failing straight away. Your API secret does not change: it is derived from your partner ID, and rotation leaves it exactly as it is. Generating credentials also rotates your webhook signing secret, so update your webhook receiver at the same time.';

const maskSecret = (value) => {
  const text = String(value ?? '');
  return text.slice(0, 4) + '•'.repeat(Math.max(text.length - 4, 0));
};

const validityLabel = (expiresAt) => {
  if (!expiresAt) return null;
  const expiry = new Date(`${dateOnly(expiresAt)}T00:00:00Z`);
  if (Number.isNaN(expiry.getTime())) return null;
  const days = Math.ceil((expiry.getTime() - Date.now()) / 86400000);
  if (days <= 0) return 'expired';
  return days === 1 ? '1 day remaining' : `${days} days remaining`;
};

const CredentialRow = ({ label, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 py-3">
    <dt className="text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">{label}</dt>
    <dd className="flex items-center gap-2">{children}</dd>
  </div>
);

const ApiSettings = ({ partnerDetails, canRotate, fetchDetails }) => {
  const [rotated, setRotated] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState('');

  // fetchDetails swallows its own errors, so the rotate response is what keeps the new key on
  // screen when the follow-up GET /details fails — the previous key authenticates nothing.
  const credentials = rotated || partnerDetails || {};
  const parentKey = partnerDetails?.api_key;

  // A key arriving from /details supersedes the response being held.
  useEffect(() => {
    setRotated((held) => (held && parentKey && parentKey !== held.api_key ? null : held));
  }, [parentKey]);

  const rotate = async () => {
    setError('');
    setRotating(true);
    try {
      const response = await api.post(PartnerEndpoints.generateCredentials, {});
      setRotated(response.data?.data ?? null);
      // The same call rotates webhook_secret, so the whole details payload has to come back.
      await fetchDetails?.();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to generate API credentials. Please try again.'));
    }
    setRotating(false);
  };

  const validity = validityLabel(credentials?.expires_at);

  return (
    <div className="space-y-4">
      <Alert variant="warning">{ROTATION_WARNING}</Alert>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      {rotating ? (
        <div className="space-y-3 py-3">
          <Skeleton h="1.1rem" />
          <Skeleton h="1.1rem" />
          <Skeleton w="60%" h="1.1rem" />
        </div>
      ) : credentials?.api_key ? (
        <>
          <dl className="divide-y divide-theme-border dark:divide-themedark-border">
            <CredentialRow label="API key">
              <code className="font-medium text-theme-headings dark:text-themedark-headings">
                {maskSecret(credentials.api_key)}
              </code>
              <CopyButton text={credentials.api_key} label="Copy key" />
            </CredentialRow>
            <CredentialRow label="API secret">
              <code className="font-medium text-theme-headings dark:text-themedark-headings">
                {maskSecret(credentials.api_secret)}
              </code>
              <CopyButton text={credentials.api_secret} label="Copy secret" />
            </CredentialRow>
            <CredentialRow label="Key expires">
              <span className="font-medium text-theme-headings dark:text-themedark-headings">
                {formatLagos(credentials.expires_at)}
              </span>
              {validity && (
                <span className="text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
                  ({validity})
                </span>
              )}
            </CredentialRow>
          </dl>
          {canRotate && (
            <div className="flex justify-end">
              <Button variant="outline-danger" onClick={rotate} loading={rotating}>
                Rotate API key
              </Button>
            </div>
          )}
        </>
      ) : (
        <EmptyState
          icon={<KeyRound className="w-8 h-8" />}
          title="No API credentials yet"
          message="Generate a key to start calling the server API."
          action={
            <Button onClick={rotate} loading={rotating}>
              Generate credentials
            </Button>
          }
        />
      )}
    </div>
  );
};

export default ApiSettings;
